import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { canAccessAdminPortal } from "../utils/adminRoles";
import "./TerminalRecoveryControls.css";

const LIMIT = 25;
const unresolved = (row) => ["needs_review", "pending_authorized_recovery"].includes(row.status);
const validId = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
const display = (value) => typeof value === "string" && value ? value : "Not recorded";
const errorText = (error, resolving) => {
	const code = String(error?.code || "").replace(/^functions\//, "");
	if (["unauthenticated", "permission-denied"].includes(code)) return "Scerv admin access is required. Sign in with an authorized support account.";
	if (code === "failed-precondition") return resolving ? "Canonical settlement is not confirmed complete. The hold remains unresolved; review the original payment and authorized recovery before checking again." : "Recovery list query or scope is unavailable. Reload the queue; reader health is unknown.";
	if (code === "not-found") return "Recovery record or endpoint unavailable. Reload the queue; deployment availability is not verified.";
	return resolving ? "Resolution outcome unconfirmed. Reload the queue before another verification." : "Could not load the recovery queue. Reload to try again.";
};

function RecoveryQueue({ restaurantId, projectId, client, user }) {
	const [records, setRecords] = useState(null);
	const [nextCursor, setNextCursor] = useState(null);
	const [selectedId, setSelectedId] = useState("");
	const [reason, setReason] = useState("");
	const [busy, setBusy] = useState(false);
	const [blocked, setBlocked] = useState(true);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const generation = useRef({ value: 0 });
	const alive = useRef(false);
	const pending = useRef(false);
	const current = useCallback((ticket) => alive.current && ticket === generation.current.value && auth.currentUser === user &&
		functions === client && functions.app?.options?.projectId === projectId, [client, projectId, user]);
	const authorize = useCallback(async (ticket) => {
		if (!current(ticket)) return false;
		const token = await getIdTokenResult(user);
		if (!current(ticket)) return false;
		const role = String(token.claims.role || "");
		if (!canAccessAdminPortal(role) || role !== role.trim()) throw Object.assign(new Error("Denied"), { code: "permission-denied" });
		return true;
	}, [current, user]);
	const loadPage = useCallback(async ({ cursor = null, afterId = "" } = {}) => {
		if (pending.current || !current(generation.current.value)) return;
		const ticket = ++generation.current.value;
		pending.current = true;
		setBusy(true); setBlocked(true); setSelectedId(""); setReason(""); setError(""); setMessage("");
		if (!cursor) { setRecords(null); setNextCursor(null); }
		try {
			if (!await authorize(ticket)) return;
			const response = await httpsCallable(client, "listScervTerminalRecovery")({ restaurantId, limit: LIMIT, ...(cursor ? { cursor } : {}) });
			if (!current(ticket)) return;
			const rows = response.data?.records;
			// Reject a mixed-tenant or malformed window instead of making it actionable.
			const next = response.data?.nextCursor;
			if (response.data?.restaurantId !== restaurantId || response.data?.firebaseProject !== projectId ||
				!Array.isArray(rows) || rows.length > LIMIT ||
				rows.some((row, index) => !row || !validId(row.id) || row.id <= (index ? rows[index - 1].id : afterId) ||
					row.restaurantId !== restaurantId || typeof row.status !== "string") ||
				!(next === null || (typeof next === "string" && next.length <= 1024 && /^[A-Za-z0-9_-]+$/.test(next) &&
					rows.length === LIMIT && next !== cursor))) {
				throw new Error("Invalid recovery response");
			}
			setRecords((previous) => cursor ? [...previous, ...rows] : rows); setNextCursor(next); setBlocked(false);
		} catch (err) {
			if (current(ticket)) {
				setError(errorText(err, false));
				if (["permission-denied", "unauthenticated"].includes(String(err?.code || "").replace(/^functions\//, ""))) {
					setRecords(null); setNextCursor(null);
				}
			}
		} finally {
			if (current(ticket)) { pending.current = false; setBusy(false); }
		}
	}, [authorize, client, current, projectId, restaurantId]);
	useEffect(() => {
		const counter = generation.current;
		alive.current = true;
		loadPage();
		return () => { alive.current = false; counter.value++; pending.current = false; };
	}, [loadPage]);

	const selected = records?.find((row) => row.id === selectedId);
	const verify = async (event) => {
		event.preventDefault();
		if (pending.current || blocked || !selected || !unresolved(selected) || !current(generation.current.value)) return;
		setError(""); setMessage("");
		if (!reason.trim() || reason.length > 240 || Array.from(reason).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) {
			setError("Enter a support reason of 1 to 240 characters without control characters."); return;
		}
		const ticket = ++generation.current.value;
		pending.current = true; setBusy(true);
		try {
			if (!await authorize(ticket)) return;
			const response = await httpsCallable(client, "resolveScervTerminalRecovery")({ restaurantId, recoveryId: selected.id, reason: reason.trim() });
			if (!current(ticket)) return;
			if (response.data?.resolved !== true) throw new Error("Unconfirmed resolution");
			setRecords((rows) => rows.map((row) => row.id === selected.id ? { ...row, status: "resolved" } : row));
			setSelectedId(""); setReason("");
			setMessage("Server verified completed canonical settlement. Recovery hold resolved.");
		} catch (err) {
			if (current(ticket)) {
				setBlocked(true); setError(errorText(err, true));
				if (["permission-denied", "unauthenticated"].includes(String(err?.code || "").replace(/^functions\//, ""))) {
					setRecords(null); setNextCursor(null); setSelectedId(""); setReason("");
				}
			}
		} finally {
			if (current(ticket)) { pending.current = false; setBusy(false); }
		}
	};

	return <section className="terminal-recovery-controls" aria-labelledby="terminal-recovery-heading" aria-busy={busy}>
		<div className="terminal-recovery-header">
			<h2 id="terminal-recovery-heading">Terminal settlement recovery</h2>
			<button type="button" disabled={busy} onClick={() => loadPage()}>Reload queue</button>
		</div>
		<p>{projectId} / {restaurantId}</p>
		<p>Reader connection health: Unknown. Settlement recovery holds do not measure reader connectivity or readiness.</p>
		<p>Only completed canonical settlement can resolve a hold. Verification does not capture a payment, close a check, or dismiss an unresolved hold.</p>
		{error && <p role="alert" className="restaurant-error">{error}</p>}
		{message && <p role="status" className="restaurant-message">{message}</p>}
		{busy && <p role="status">Checking recovery...</p>}
		{records && <p>{records.length} settlement records loaded. {busy || error ? "List incomplete or unconfirmed." : nextCursor ? "More holds available." : "End of list as of the last page request."}</p>}
		{records?.length === 0 && <p>No recovery holds returned for this restaurant. Reader connection health remains unknown.</p>}
		{!!records?.length && <div className="terminal-recovery-table-scroll"><table>
			<thead><tr><th scope="col">Selection</th><th scope="col">Payment</th><th scope="col">Status / reason</th><th scope="col">Owner</th></tr></thead>
			<tbody>{records.map((row) => <tr key={row.id}>
				<td><input type="radio" name="terminal-recovery-selection" aria-label={`Select recovery ${row.id}`} checked={selectedId === row.id}
					disabled={busy || blocked || !unresolved(row)} onChange={() => { setSelectedId(row.id); setReason(""); setMessage(""); setError(""); }} /></td>
				<td>{display(row.paymentIntentId)}</td><td>{row.status}<br />{display(row.reason)}</td><td>{display(row.owner)}</td>
			</tr>)}</tbody>
		</table></div>}
		{nextCursor && <button type="button" disabled={busy || blocked} onClick={() => loadPage({ cursor: nextCursor, afterId: records[records.length - 1].id })}>Load more</button>}
		{selected && <>
			<dl>
				{[["Recovery ID", selected.id], ["Restaurant", selected.restaurantId], ["Party", selected.partyId],
					["Payment intent", selected.paymentIntentId], ["Checkout attempt", selected.checkoutAttemptId],
					["Source", selected.sourceType], ["Firebase project", selected.firebaseProject],
					["Connected account", selected.connectedAccountId], ["Mode", selected.liveMode === true ? "Live" : selected.liveMode === false ? "Test" : null],
					["Provider status", selected.providerStatus], ["Received (minor units)", Number.isSafeInteger(selected.amountReceived) ? String(selected.amountReceived) : null],
					["Created", selected.createdAt], ["Owner", selected.owner],
					["Next action", selected.nextAction]].map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{display(value)}</dd></React.Fragment>)}
			</dl>
			<form onSubmit={verify} noValidate>
				<label htmlFor="terminal-recovery-reason">Support reason</label>
				<input id="terminal-recovery-reason" value={reason} maxLength={240} disabled={busy || blocked} autoComplete="off" onChange={(event) => setReason(event.target.value)} />
				<button type="submit" disabled={busy || blocked || !reason.trim() || !unresolved(selected)}>Verify resolution</button>
			</form>
		</>}
	</section>;
}

export default function TerminalRecoveryControls({ restaurantId }) {
	const [user, loading, error] = useAuthState(auth);
	const projectId = functions.app?.options?.projectId;
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account.</p>;
	if (!validId(restaurantId) || !validId(projectId)) return <p role="alert">A valid restaurant and project are required.</p>;
	return <RecoveryQueue key={`${projectId}:${restaurantId}:${user.uid}`} restaurantId={restaurantId} projectId={projectId} client={functions} user={user} />;
}
